import { useEffect, useRef, useState } from 'react';
import { announcerAdminApi, type AnnouncerData, type AnnouncerPhraseData } from '../../../api/adminApi';
import { announcerArtUrl } from '../../../api/announcerApi';
import { useTranslation } from '../../../i18n/useTranslation';

// ─────────────────────────────────────────────────────────────────────────────
// Locutores — /admin/announcers
// Quem narra o quadro de fim de batalha/missão (reino na vitória, monstro na
// derrota) e as falas sorteadas. Criar, editar, trocar imagem e excluir.
// ─────────────────────────────────────────────────────────────────────────────

type Tab = 'announcers' | 'phrases';
type KindFilter = 'all' | 'victory' | 'defeat';

export function AnnouncersPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('announcers');
  const [announcers, setAnnouncers] = useState<AnnouncerData[]>([]);
  const [phrases, setPhrases] = useState<AnnouncerPhraseData[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const reload = () =>
    announcerAdminApi.list()
      .then(r => { setAnnouncers(r.announcers); setPhrases(r.phrases); setError(''); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));

  useEffect(() => { void reload(); }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setError('');
    try { await fn(); await reload(); } catch (e) { setError((e as Error).message); }
  };

  return (
    <>
      <div className="adminHeader">
        <h1>📣 {t('announcers.title')}</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className={`btn ${tab === 'announcers' ? 'btnPrimary' : ''}`} onClick={() => setTab('announcers')}>
            {t('announcers.tab.announcers')} ({announcers.length})
          </button>
          <button className={`btn ${tab === 'phrases' ? 'btnPrimary' : ''}`} onClick={() => setTab('phrases')}>
            {t('announcers.tab.phrases')} ({phrases.length})
          </button>
        </div>
      </div>
      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}
        {loading ? <p style={{ color: 'var(--text-dim)' }}>…</p>
          : tab === 'announcers'
            ? <AnnouncersTab list={announcers} run={run} />
            : <PhrasesTab list={phrases} run={run} />}
      </div>
    </>
  );
}

// ── Locutores ────────────────────────────────────────────────────────────────

function AnnouncersTab({ list, run }: { list: AnnouncerData[]; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <button className="btn btnPrimary"
          onClick={() => void run(() => announcerAdminApi.create({ name: t('announcers.new'), side: 'hero', image: '', active: true }))}>
          + {t('announcers.new')}
        </button>
        <span style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-dim)' }}>{t('announcers.imageHint')}</span>
      </div>
      {list.length === 0 && <p style={{ color: 'var(--text-dim)' }}>{t('announcers.empty')}</p>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 16 }}>
        {list.map(a => <AnnouncerCard key={a.id} a={a} run={run} />)}
      </div>
    </>
  );
}

function AnnouncerCard({ a, run }: { a: AnnouncerData; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(a);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => setDraft(a), [a]);
  const dirty = draft.name !== a.name || draft.side !== a.side || draft.active !== a.active;

  return (
    <div className="adminCard" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10, opacity: draft.active ? 1 : 0.6 }}>
      <div style={{
        height: 170, borderRadius: 8, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', overflow: 'hidden',
        background: draft.side === 'hero' ? 'radial-gradient(ellipse at bottom, rgba(232,196,92,.25), transparent 70%)' : 'radial-gradient(ellipse at bottom, rgba(220,60,50,.25), transparent 70%)',
      }}>
        {a.image
          ? <img src={announcerArtUrl(a.image)} alt={a.name} style={{ height: '100%', objectFit: 'contain' }} />
          : <span style={{ alignSelf: 'center', color: 'var(--text-dim)', fontSize: 12 }}>{t('announcers.noImage')}</span>}
      </div>
      <input className="formInput" value={draft.name} maxLength={60}
        onChange={e => setDraft({ ...draft, name: e.target.value })} aria-label={t('announcers.name')} />
      <select className="formInput" value={draft.side} onChange={e => setDraft({ ...draft, side: e.target.value as AnnouncerData['side'] })}>
        <option value="hero">{t('announcers.side.hero')}</option>
        <option value="monster">{t('announcers.side.monster')}</option>
      </select>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} />
        {t('announcers.active')}
      </label>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" hidden
        onChange={e => {
          const f = e.target.files?.[0];
          if (f && a.id) void run(() => announcerAdminApi.uploadImage(a.id!, f));
          e.target.value = '';
        }} />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => fileRef.current?.click()}>🖼 {t('announcers.image')}</button>
        <button className="btn btnPrimary" disabled={!dirty}
          onClick={() => void run(() => announcerAdminApi.update(a.id!, { name: draft.name, side: draft.side, active: draft.active }))}>
          {t('announcers.save')}
        </button>
        <button className="btn" style={{ color: 'var(--danger, #ff6b6b)' }}
          onClick={() => { if (confirm(t('announcers.confirmDelete').replace('{name}', a.name))) void run(() => announcerAdminApi.remove(a.id!)); }}>
          {t('announcers.delete')}
        </button>
      </div>
    </div>
  );
}

// ── Falas ────────────────────────────────────────────────────────────────────

function PhrasesTab({ list, run }: { list: AnnouncerPhraseData[]; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<KindFilter>('all');
  const [draft, setDraft] = useState<AnnouncerPhraseData>({ kind: 'victory', text_pt: '', text_en: '', active: true });
  const shown = list.filter(p => filter === 'all' || p.kind === filter);

  return (
    <>
      <div className="adminCard" style={{ padding: 14, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong>+ {t('announcers.newPhrase')}</strong>
        <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{t('announcers.placeholders')}</span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="formInput" style={{ width: 160 }} value={draft.kind}
            onChange={e => setDraft({ ...draft, kind: e.target.value as AnnouncerPhraseData['kind'] })}>
            <option value="victory">{t('announcers.kind.victory')}</option>
            <option value="defeat">{t('announcers.kind.defeat')}</option>
          </select>
          <input className="formInput" style={{ flex: 2, minWidth: 220 }} placeholder={t('announcers.textPt')} maxLength={240}
            value={draft.text_pt} onChange={e => setDraft({ ...draft, text_pt: e.target.value })} />
          <input className="formInput" style={{ flex: 2, minWidth: 220 }} placeholder={t('announcers.textEn')} maxLength={240}
            value={draft.text_en ?? ''} onChange={e => setDraft({ ...draft, text_en: e.target.value })} />
          <button className="btn btnPrimary" disabled={!draft.text_pt.trim()}
            onClick={() => void run(async () => {
              await announcerAdminApi.createPhrase(draft);
              setDraft({ ...draft, text_pt: '', text_en: '' });
            })}>
            {t('announcers.add')}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(['all', 'victory', 'defeat'] as KindFilter[]).map(k => (
          <button key={k} className={`btn ${filter === k ? 'btnPrimary' : ''}`} onClick={() => setFilter(k)}>
            {k === 'all' ? t('announcers.filter.all') : t(`announcers.kind.${k}`)}
            {' '}({k === 'all' ? list.length : list.filter(p => p.kind === k).length})
          </button>
        ))}
      </div>

      {shown.length === 0 && <p style={{ color: 'var(--text-dim)' }}>{t('announcers.empty')}</p>}
      <div className="adminCard">
        {shown.map(p => <PhraseRow key={p.id} p={p} run={run} />)}
      </div>
    </>
  );
}

function PhraseRow({ p, run }: { p: AnnouncerPhraseData; run: (fn: () => Promise<unknown>) => Promise<void> }) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(p);
  useEffect(() => setDraft(p), [p]);
  const dirty = draft.text_pt !== p.text_pt || (draft.text_en ?? '') !== (p.text_en ?? '') || draft.kind !== p.kind || draft.active !== p.active;

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', borderBottom: '1px solid var(--border)', opacity: draft.active ? 1 : 0.55 }}>
      <select className="formInput" style={{ width: 120 }} value={draft.kind}
        onChange={e => setDraft({ ...draft, kind: e.target.value as AnnouncerPhraseData['kind'] })}>
        <option value="victory">🏆 {t('announcers.kind.victory')}</option>
        <option value="defeat">💀 {t('announcers.kind.defeat')}</option>
      </select>
      <input className="formInput" style={{ flex: 3, minWidth: 240 }} value={draft.text_pt} maxLength={240}
        onChange={e => setDraft({ ...draft, text_pt: e.target.value })} aria-label={t('announcers.textPt')} />
      <input className="formInput" style={{ flex: 2, minWidth: 180 }} value={draft.text_en ?? ''} maxLength={240}
        placeholder={t('announcers.textEn')} onChange={e => setDraft({ ...draft, text_en: e.target.value })} />
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
        <input type="checkbox" checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} />
        {t('announcers.active')}
      </label>
      <button className="btn btnPrimary" disabled={!dirty || !draft.text_pt.trim()}
        onClick={() => void run(() => announcerAdminApi.updatePhrase(p.id!, draft))}>
        {t('announcers.save')}
      </button>
      <button className="btn" style={{ color: 'var(--danger, #ff6b6b)' }}
        onClick={() => { if (confirm(t('announcers.confirmDeletePhrase'))) void run(() => announcerAdminApi.removePhrase(p.id!)); }}>
        {t('announcers.delete')}
      </button>
    </div>
  );
}
